/**
 * Hook for loading Pixlee script globally
 * Singleton pattern ensures script is loaded only once
 * Multiple components can safely call this hook
 */
import {useEffect, useState} from 'react';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';

/**
 * Global script manager - Singleton pattern
 * Ensures script is loaded only once across all components
 */
const pixleeScriptManager = {
  loading: false,
  ready: false,
  script: null,
  waiters: [],
  loadPromise: null,

  /**
   * Load Pixlee script once
   * Returns promise that resolves when script is ready
   */
  async load(apiKey) {
    // Already ready - return immediately
    if (this.ready && window.Pixlee) {
      return Promise.resolve();
    }

    // Already loading - wait for existing load
    if (this.loading && this.loadPromise) {
      return this.loadPromise;
    }

    // Validate API key
    if (!apiKey) {
      const error = new Error('PIXLEE_API_KEY is not configured in site preferences');

      console.error(error.message);
      this.waiters.forEach((waiter) => waiter.reject(error));
      this.waiters = [];
      throw error;
    }

    // Start loading
    this.loading = true;
    this.loadPromise = new Promise((resolve, reject) => {
      // Check if already exists (from previous load)
      if (window.Pixlee) {
        this.ready = true;
        this.loading = false;
        resolve();

        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector('script[src*="pixlee_widget"]');

      if (existingScript) {
        // Wait for existing script to load
        existingScript.addEventListener('load', () => {
          if (window.Pixlee) {
            window.Pixlee.init({apiKey});
            this.ready = true;
            this.loading = false;
            resolve();
          }
        });
        existingScript.addEventListener('error', reject);

        return;
      }

      // Create new script tag
      const script = document.createElement('script');

      script.src = 'https://assets.pxlecdn.com/assets/pixlee_widget_1_0_0.js';
      script.async = true;
      script.onload = () => {
        if (window.Pixlee) {
          window.Pixlee.init({apiKey});
          this.ready = true;
          this.loading = false;
          this.script = script;
          resolve();
          // Resolve all waiters
          this.waiters.forEach((waiter) => waiter.resolve());
          this.waiters = [];
        } else {
          const error = new Error('Pixlee failed to initialize');

          this.loading = false;
          reject(error);
          // Reject all waiters
          this.waiters.forEach((waiter) => waiter.reject(error));
          this.waiters = [];
        }
      };
      script.onerror = () => {
        const error = new Error('Failed to load Pixlee script');

        this.loading = false;
        reject(error);
        // Reject all waiters
        this.waiters.forEach((waiter) => waiter.reject(error));
        this.waiters = [];
      };

      this.script = script;
      document.body.appendChild(script);
    });

    return this.loadPromise;
  },

  /**
   * Get current ready state
   */
  isReady() {
    return this.ready && !!window.Pixlee;
  },
};

/**
 * Load and initialize Pixlee script globally
 * Safe to call from multiple components - script loads only once
 *
 * @returns {boolean} isReady - True when Pixlee is loaded and initialized
 */
export function usePixleeScript() {
  const [isReady, setIsReady] = useState(() => pixleeScriptManager.isReady());
  const apiKey = useSitePreference('PIXLEE_API_KEY');

  useEffect(() => {
    // Check if already ready
    if (pixleeScriptManager.isReady()) {
      setIsReady(true);

      return;
    }

    // No API key - can't load
    if (!apiKey) {
      return;
    }

    // Load script (singleton - safe to call multiple times)
    pixleeScriptManager.load(apiKey)
      .then(() => {
        setIsReady(true);
      })
      .catch((error) => {
        console.error('Pixlee script loading error:', error);
        setIsReady(false);
      });
  }, [apiKey]);

  return isReady;
}
